import api from "@/api/v1-jwt/config/api";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import clientSettingsQueryKeys from "./query-keys";
import { ClientSettingsResponse, ClientSettingsCategory } from "./types";

type ClientSettingsQueryKey = ReturnType<
  typeof clientSettingsQueryKeys.category
>;

interface GetClientSettingsParams {
  userId: string;
  category?: ClientSettingsCategory;
}

const getClientSettings = async ({
  userId,
  category,
}: GetClientSettingsParams): Promise<ClientSettingsResponse> => {
  const categoryParam = category ? `?category=${category}` : "";
  const url = `/platform/client-settings/${userId}${categoryParam}`;
  const { data } = await api.get(url);
  return data.data || [];
};

export const useClientSettingsOptions = <TSelected = ClientSettingsResponse>(
  { userId, category }: GetClientSettingsParams,
  options?: Omit<
    UseQueryOptions<ClientSettingsResponse, Error, TSelected, ClientSettingsQueryKey>,
    "queryKey" | "queryFn"
  >
): UseQueryOptions<
  ClientSettingsResponse,
  Error,
  TSelected,
  ClientSettingsQueryKey
> => {
  const defaultOptions: UseQueryOptions<
    ClientSettingsResponse,
    Error,
    TSelected,
    ClientSettingsQueryKey
  > = {
    queryKey: clientSettingsQueryKeys.category(userId, category),
    queryFn: () => getClientSettings({ userId, category }),
    staleTime: 60 * 60 * 1000,
    enabled: options?.enabled ?? !!userId,
  };
  return { ...defaultOptions, ...options };
};

export const useClientSettings = <TSelected = ClientSettingsResponse>(
  { userId, category }: GetClientSettingsParams,
  options?: Omit<
    UseQueryOptions<ClientSettingsResponse, Error, TSelected, ClientSettingsQueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const queryOptions = useClientSettingsOptions<TSelected>(
    { userId, category },
    options
  );
  return useQuery<ClientSettingsResponse, Error, TSelected, ClientSettingsQueryKey>({
    ...queryOptions,
  });
};
